import { createRouter, createMemoryHistory, createWebHistory, type RouteRecordRaw, type Router } from 'vue-router'

// SSR-safe route definitions
export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('../views/Home.vue'),
    meta: {
      title: 'WebRTC QR Tool - Home',
      description: 'WebRTC QR Tool - Serverless WebRTC connections using QR codes for signaling'
    }
  },
  {
    path: '/demo',
    name: 'demo',
    component: () => import('../views/Demo.vue'),
    meta: {
      title: 'WebRTC QR Tool - Interactive Demo',
      description: 'Try the WebRTC QR connection demo'
    }
  },
  {
    path: '/docs',
    name: 'docs',
    component: () => import('../views/Documentation.vue'),
    meta: {
      title: 'WebRTC QR Tool - Documentation',
      description: 'Learn how to use the WebRTC QR connection system'
    }
  },
  {
    path: '/webrtc-demo',
    name: 'webrtc-demo',
    component: () => import('../views/WebRTCDemo.vue'),
    meta: {
      title: 'WebRTC QR Tool - Real Demo',
      description: 'Test real WebRTC peer-to-peer connections'
    }
  },
  {
    path: '/generator',
    name: 'generator',
    component: () => import('../views/Generator.vue'),
    meta: {
      title: 'WebRTC QR Tool - QR Generator',
      description: 'Generate QR codes for WebRTC connections'
    }
  },
  {
    path: '/scanner',
    name: 'scanner',
    component: () => import('../views/Scanner.vue'),
    meta: {
      title: 'WebRTC QR Tool - QR Scanner',
      description: 'Scan QR codes for WebRTC connections'
    }
  },
  {
    path: '/chat',
    name: 'chat',
    component: () => import('../views/Chat.vue'),
    meta: {
      title: 'WebRTC QR Tool - Chat',
      description: 'Chat interface for WebRTC connections'
    }
  },
  {
    path: '/file-transfer',
    name: 'file-transfer',
    component: () => import('../views/FileTransfer.vue'),
    meta: {
      title: 'WebRTC QR Tool - File Transfer',
      description: 'Transfer files via WebRTC connections'
    }
  },
  {
    path: '/telepresence',
    name: 'telepresence',
    component: () => import('../views/TelepresenceInspection.vue'),
    meta: {
      title: 'WebRTC In-Task Telepresence & Attestation',
      description: 'Cryptographic remote inspection and live frame attestation'
    }
  },
  {
    path: '/telepresence/:taskId',
    name: 'telepresence-task',
    component: () => import('../views/TelepresenceInspection.vue'),
    props: true,
    meta: {
      title: 'WebRTC In-Task Telepresence & Attestation',
      description: 'Cryptographic remote inspection and live frame attestation for task'
    }
  },
  {
    path: '/video-conference',
    name: 'video-conference',
    component: () => import('../views/MultiParticipantVideoConference.vue'),
    props: (route) => ({ ...route.params, mode: 'single' }),
    meta: {
      title: 'WebRTC QR Tool - Video Conference',
      description: 'Full-featured video conference room like Google Meet'
    }
  },
  {
    path: '/join/:roomId',
    name: 'join-room',
    component: () => import('../views/MultiParticipantVideoConference.vue'),
    props: (route) => ({ ...route.params, mode: 'single' }),
    meta: {
      title: 'WebRTC QR Tool - Join Video Conference',
      description: 'Join a video conference room'
    }
  },
  {
    path: '/multi-video-conference',
    name: 'multi-video-conference',
    component: () => import('../views/MultiParticipantVideoConference.vue'),
    props: (route) => ({ ...route.params, mode: 'multi' }),
    meta: {
      title: 'WebRTC QR Tool - Multi-User Video Conference',
      description: 'Multi-user video conference with QR code signaling'
    }
  },
  {
    path: '/multi-video-conference/:roomId',
    name: 'multi-video-conference-join',
    component: () => import('../views/MultiParticipantVideoConference.vue'),
    props: (route) => ({ ...route.params, mode: 'multi' }),
    meta: {
      title: 'WebRTC QR Tool - Join Multi-User Video Conference',
      description: 'Join a multi-user video conference room'
    }
  },
  {
    path: '/conference',
    name: 'conference',
    component: () => import('../views/MultiParticipantVideoConference.vue'),
    meta: {
      title: 'WebRTC QR Tool - Video Conference',
      description: 'Unified video conference interface with single and multi-participant modes'
    }
  },
  {
    path: '/conference/:mode',
    name: 'conference-mode',
    component: () => import('../views/MultiParticipantVideoConference.vue'),
    props: (route) => ({ ...route.params, mode: route.params.mode || 'multi' }),
    meta: {
      title: 'WebRTC QR Tool - Video Conference Mode',
      description: 'Video conference with specified mode'
    }
  },
  {
    path: '/conference/:mode/:roomId',
    name: 'conference-join',
    component: () => import('../views/MultiParticipantVideoConference.vue'),
    props: (route) => ({ ...route.params, mode: route.params.mode || 'multi' }),
    meta: {
      title: 'WebRTC QR Tool - Join Video Conference',
      description: 'Join a video conference room with specified mode'
    }
  },
  {
    path: '/demo/:mode',
    name: 'demo-mode',
    component: () => import('../views/Demo.vue'),
    props: true,
    meta: {
      title: 'WebRTC QR Tool - Demo Mode',
      description: 'WebRTC QR connection demo mode'
    }
  },
  {
    path: '/security',
    name: 'security',
    component: () => import('../views/SecurityDashboard.vue'),
    meta: {
      title: 'Enterprise Security Dashboard',
      description: 'Monitor and manage enterprise-grade security features'
    }
  },
  {
    // Elveris sign-in redirect target (OIDC authorization code + PKCE).
    path: '/callback',
    name: 'auth-callback',
    component: () => import('../views/AuthCallback.vue'),
    meta: {
      title: 'WebRTC QR Tool - Signing in',
      description: 'Completing Elveris sign-in'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/Home.vue'),
    meta: {
      title: 'WebRTC QR Tool - Page Not Found',
      description: 'The requested page was not found'
    }
  }
]

// Environment detection utility
export function isClient(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

// SSR-safe router factory
export function createSSRRouter(base: string = '/'): Router {
  return createRouter({
    history: createMemoryHistory(base),
    routes
  })
}

// Client router factory (only use in client code)
export function createClientRouter(base: string = '/'): Router {
  // Use web history for proper client-side navigation
  return createRouter({
    history: createWebHistory(base),
    routes
  })
}

// Export default router creator for backward compatibility
export default createSSRRouter